/**
 * Suppress non-critical console errors in production
 * This helps reduce noise from known benign warnings
 */

// List of error patterns to suppress
const SUPPRESSED_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
];

/**
 * Initialize error suppression for production environment
 * Call this once at app startup
 */
export function initErrorSuppression() {
  if (import.meta.env.PROD) {
    const originalError = console.error;
    const originalWarn = console.warn;

    console.error = (...args: any[]) => {
      const message = args[0]?.toString?.() || '';
      
      // Check if this error should be suppressed
      const shouldSuppress = SUPPRESSED_ERRORS.some(pattern => 
        message.includes(pattern)
      );

      if (!shouldSuppress) {
        originalError.apply(console, args);
      }
    };

    console.warn = (...args: any[]) => {
      const message = args[0]?.toString?.() || '';
      
      // Check if this warning should be suppressed
      const shouldSuppress = SUPPRESSED_ERRORS.some(pattern => 
        message.includes(pattern)
      );

      if (!shouldSuppress) {
        originalWarn.apply(console, args);
      }
    };
  }
}

/**
 * Wrap a function to catch and suppress ResizeObserver errors
 */
export function suppressResizeObserverErrors<T extends (...args: any[]) => any>(
  fn: T
): T {
  return ((...args: any[]) => {
    try {
      return fn(...args);
    } catch (error: any) {
      // Only suppress ResizeObserver errors
      if (
        error?.message?.includes('ResizeObserver') ||
        error?.name === 'ResizeObserver'
      ) {
        // Silently ignore
        return;
      }
      // Re-throw other errors
      throw error;
    }
  }) as T;
}
